import type { TypeInstanceMap } from '@lighthouse/core'

import { FunctionObject, MethodList } from '../../FormulaView/types'

const numberFunction: FunctionObject[] = [
    {
        id: 'number_max',
        name: 'Max',
        fn: 'Max()',
        type: 'number',
        class: 'function',
        title: 'Max(num...)',
        describe: '找到一组数值中的最大值',
        usage: [
            {
                title: '示例',
                examples: ['Max(1, 2, 3, 4, 5)  #输出为 5']
            }
        ],
        input: [
            {
                title: 'num, ...',
                content: '表示任意多个数值类型的参数'
            }
        ],
        output: '返回参数中的最大值，数值类型'
    },
    {
        id: 'number_min',
        name: 'Min',
        fn: 'Min()',
        type: 'number',
        class: 'function',
        title: 'Min(num, ...)',
        describe: '找到一组数值中的最小值',
        usage: [
            {
                title: '示例',
                examples: ['Min(1, 2, 3, 4, 5)  #输出为 1']
            }
        ],
        input: [
            {
                title: 'num, ...',
                content: '表示任意多个数值类型的参数'
            }
        ],
        output: '返回参数中的最小值，数值类型'
    },
    {
        id: 'number_round',
        name: 'Round',
        fn: 'Round()',
        type: 'number',
        class: 'function',
        title: 'Round(num, precision)',
        describe: '按指定的位数对数组进行四舍五入',
        usage: [
            {
                title: '示例',
                examples: ['Round(123.78, 0)  #输出为 124', 'Round(123.78, 1)  #输出为 123.8', 'Round(123.78, -1)  #输出为 120']
            }
        ],
        input: [
            {
                title: 'num',
                content: '需要进行四舍五入的值，为数值类型'
            },
            {
                title: 'precision',
                content:
                    '非必填，表示要进行四舍五入运算的位数，为数值类型整数。如果位数大于 0，则四舍五入到指定的小数位，如果位数等于 0，则四舍五入到最接近的整数。如果位数小于 0，则在小数点左侧进行四舍五入'
            }
        ],
        output: '返回四舍五入后的值'
    },
    {
        id: 'number_avg',
        name: 'Avg',
        fn: 'Avg()',
        type: 'number',
        title: 'Avg(num, ...)',
        class: 'function',
        describe: '计算一组数值的平均值',
        usage: [
            {
                title: '示例',
                examples: ['Avg(1, 2, 3, 4, 5)  #输出为 3']
            }
        ],
        input: [
            {
                title: 'num, ...',
                content: '表示任意多个数值类型的参数'
            }
        ],
        output: '返回参数中所有数值的平均值，数值类型'
    },

    {
        id: 'number_sum',
        name: 'Sum',
        fn: 'Sum()',
        type: 'number',
        class: 'function',
        title: 'Sum(num, ...)',
        describe: '计算一组数值的总和',
        usage: [
            {
                title: '示例',
                examples: ['Sum(1, 2, 3, 4, 5)  #输出为 15']
            }
        ],
        input: [
            {
                title: 'num, ...',
                content: '表示任意多个数值类型的参数'
            }
        ],
        output: '返回参数中所有数值的总和，数值类型'
    },
    {
        id: 'number_roundUp',
        name: 'RoundUp',
        fn: 'RoundUp()',
        type: 'number',
        class: 'function',
        title: 'RoundUp(num, [precision])',
        describe: '将数值朝绝对值增大方向舍入到最接近的整数',
        usage: [
            {
                title: '示例',
                examples: ['RoundUp(1.2)  #输出为 2', 'RoundUp(1.11, 1)  #输出为 1.2', 'RoundUp(123.21, -1)  #输出为 130']
            }
        ],
        input: [
            {
                title: 'num',
                content: '被舍入的值，数值类型'
            },
            {
                title: 'precision',
                content: '舍入的精度，数值类型的整数，可选，默认为0'
            }
        ],
        output: '返回舍入后的值，数值类型'
    },
    {
        id: 'number_roundDown',
        name: 'RoundDown',
        fn: 'RoundDown()',
        type: 'number',
        class: 'function',
        title: 'RoundDown(num, [precision])',
        describe: '将数值朝绝对值减小方向舍入到最接近的整数',
        usage: [
            {
                title: '示例',
                examples: ['RoundUp(1.2)  #输出为 1', 'RoundUp(1.11, 1)  #输出为 1.10', 'RoundUp(123.21, -1)  #输出为 120']
            }
        ],
        input: [
            {
                title: 'num',
                content: '被舍入的值，数值类型'
            },
            {
                title: 'precision',
                content: '舍入的精度，数值类型的整数，可选，默认为0'
            }
        ],
        output: '返回舍入后的值，数值类型'
    },
    {
        id: 'number_toText',
        name: 'ToText',
        fn: 'ToText()',
        type: 'number',
        class: 'function',
        title: 'toText(number, format)',
        describe: '将指定数值按转换为文本类型',
        usage: [
            {
                title: '示例',
                examples: ['ToText(123.23)       #输出为 "123.23"   ', '123.23.toText()      #输出为 "123.23"']
            }
        ],
        input: [
            {
                title: 'number',
                content: '需要转换的数值，数值类型'
            },
            {
                title: 'format',
                content: [
                    '可选参数',
                    '格式化模板，为文本类型, 规则如下:',
                    'yyyy表示年份',
                    'MM表示月份',
                    'dd表示日期',
                    'HH表示24小时制的小时',
                    'hh表示12小时制的小时',
                    'mm表示分钟',
                    'ss表示秒'
                ]
            }
        ],
        output: '返回转换后的结果，值为文本类型'
    },
    {
        id: 'number_mod',
        name: 'Mod',
        fn: 'Mod()',
        type: 'number',
        class: 'function',
        title: 'mod(number, divisor)',
        describe: '返回两数相除的余数。结果的正负号与除数相同',
        usage: [
            {
                title: '示例',
                examples: ['mod(5,2)       #输出为 1   ', '5.mod(2)       #输出为 1   ']
            }
        ],
        input: [
            {
                title: 'number',
                content: '为被除数'
            },
            {
                title: 'divisor',
                content: '为除数'
            }
        ],
        output: '返回转换后的结果，值为数值类型'
    },
    {
        id: 'number_abs',
        name: 'Abs',
        fn: 'Abs()',
        type: 'number',
        class: 'function',
        title: 'abs(number)',
        describe: '返回数字的绝对值。绝对值没有符号',
        usage: [
            {
                title: '示例',
                examples: ['abs(-123)       #输出为 123   ', 'abs(123)       #输出为 123   ']
            }
        ],
        input: [
            {
                title: 'number',
                content: '需要计算其绝对值的实数'
            }
        ],
        output: '返回转换后的结果，值为数值类型'
    }
]

const textFunction: FunctionObject[] = [
    {
        id: 'text_concat',
        name: 'Concat',
        fn: 'Concat()',
        type: 'text',
        class: 'function',
        title: 'Concat(text, ...)',
        describe: '对文本内容进行拼接',
        usage: [
            {
                title: '示例',
                examples: [
                    'Concat("Hello","  ", "ByeCode")  #输出为 "Hello  ByeCode"  ',
                    '"Hello".Concat("  ", "ByeCode")  #输出为 "Hello  ByeCode"'
                ]
            }
        ],
        input: [
            {
                title: 'text, ...',
                content: '表示任意多个文本类型参数'
            }
        ],
        output: '输出经过拼接后的文本类型的值'
    },
    {
        id: 'text_startsWith',
        name: 'StartsWith',
        fn: 'StartsWith()',
        type: 'text',
        class: 'function',
        title: 'StartsWith(text, prefix)',
        describe: '判断文本是否以指定的前缀开头',
        usage: [
            {
                title: '示例',
                examples: [
                    'StartsWith("Hello ByeCode", "Hello")     #输出为 true   ',
                    '"Hello ByeCode".StartsWith("ByeCode")    #输出为 false'
                ]
            }
        ],
        input: [
            {
                title: 'text',
                content: '文本类型，需要检查的文本'
            },
            {
                title: 'prefix',
                content: '文本类型，指定的前缀'
            }
        ],
        output: '返回判断的结果,值为布尔类型'
    },
    {
        id: 'text_endsWith',
        name: 'EndsWith',
        fn: 'EndsWith()',
        type: 'text',
        class: 'function',
        title: 'EndsWith(text, suffix)',
        describe: '判断文本是否以指定的后缀结尾',
        usage: [
            {
                title: '示例',
                examples: ['EndsWith("Hello ByeCode","ByeCode")   #输出为 true', '"Hello ByeCode!".EndsWith("ByeCode")  #输出为 false']
            }
        ],
        input: [
            {
                title: 'text',
                content: '文本类型，需要检查的文本'
            },
            {
                title: 'prefix',
                content: '文本类型，指定的后缀'
            }
        ],
        output: '返回判断的结果,值为布尔类型'
    },
    // {
    //     id: 'ifBlank',
    //     name: 'IfBlank()',
    //     type: 'text',
    //     class: 'function',
    //     title: 'IfBlank(value,ifBlank)',
    //     describe: '检查给定的值是否为空，并返回一个不为空的值',
    //     usage: [
    //         {
    //             title: '示例',
    //             describe: '如果指定文本为空则返回"hello"，否则返回该文本',
    //             examples: ['IfBlank("","hello") = hello', '{文本}.ifBlank("hello”)']
    //         }
    //     ],
    //     input: [
    //         {
    //             title: 'value',
    //             content: '需要检查的值，该值为文本类型'
    //         },
    //         {
    //             title: 'ifBlank',
    //             content: '当检查的值为空时需要返回的值，该值为文本类型'
    //         }
    //     ],
    //     output: '当给定的目标值为空时,返回第二个参数的值，否则返回目标值'
    // },
    {
        id: 'text_slice',
        name: 'Slice',
        fn: 'Slice()',
        type: 'text',
        class: 'function',
        title: 'Slice(text, start, length)',
        describe: '对文本内容进行截取',
        usage: [
            {
                title: '示例',
                examples: [
                    'Slice("Hello ByeCode", 1, 5)  #输出为 "Hello"',
                    'Slice("Hello ByeCode", 7, 3)  #输出为 "Bye"',
                    '"Hello".Slice(1, 5)   #输出为 "Hello"'
                ]
            }
        ],
        input: [
            {
                title: 'text',
                content: '需要截取的文本，文本类型'
            },
            {
                title: 'start',
                content: '截取的起始位置，最小值为1，该值只能是数值类型的正整数'
            },
            {
                title: 'length',
                content: '截取的长度，该值只能是数值类型的正整数'
            }
        ],
        output: '按照参数中提供的截取规则进行截取后文本，值为文本类型'
    },
    {
        id: 'text_toNumber',
        name: 'ToNumber',
        fn: 'ToNumber()',
        type: 'text',
        class: 'function',
        title: 'ToNumber(value)',
        describe: '将文本内容转换为数值',
        usage: [
            {
                title: '示例',
                examples: [
                    'ToNumber("1234")       #输出为 1234',
                    'ToNumber("123ab4")     #输出为 1234',
                    'ToNumber("-123ab.04")  #输出为 -123.04',
                    '"-123ab.04".toNumber() #输出为 -123.04'
                ]
            }
        ],
        input: [
            {
                title: 'text',
                content: '需要转换的值，文本类型'
            }
        ],
        output: '返回转换后的值，为数值类型，在转换过程中会过滤掉文本中无法转换为数字的字符'
    },
    {
        id: 'text_toDate',
        name: 'ToDate',
        fn: 'ToDate()',
        type: 'text',
        class: 'function',
        title: 'ToDate(text, format)',
        describe: '将文本按格式转换为时间类型',
        usage: [
            {
                title: '示例',
                examples: ['ToDate("2022/02/03", "YYYY/MM/DD")  = 2022/02/03', '{文本}.ToDate("YYYY/MM/DD")']
            }
        ],
        input: [
            {
                title: 'text',
                content: '需要转换的文本，文本类型'
            },
            {
                title: 'format',
                content: [
                    '格式化模板，为文本类型, 规则如下:',
                    'yyyy表示年份',
                    'MM表示月份',
                    'dd表示日期',
                    'HH表示24小时制的小时',
                    'hh表示12小时制的小时',
                    'mm表示分钟',
                    'ss表示秒'
                ]
            }
        ],
        output: '两个日期之差的数值'
    },
    {
        id: 'text_toArray',
        name: 'ToArray',
        fn: 'ToArray()',
        type: 'text',
        class: 'function',
        title: 'ToArray(text)',
        describe: '将文本按英文逗号分隔转换为数组',
        usage: [
            {
                title: '示例',
                examples: ['ToArray("A,B,C") = ["A","B","C"]']
            }
        ],
        input: [
            {
                title: 'text',
                content: '需要转换的文本，文本类型'
            }
        ],
        output: '返回转换后的文本数组，数组类型'
    },
    {
        id: 'text_len',
        name: 'Len',
        fn: 'Len()',
        type: 'text',
        class: 'function',
        title: 'Len(text)',
        describe: '返回文本字符串中的字符数',
        usage: [
            {
                title: '示例',
                examples: ['"qw e002".Len()  #输出为 7']
            },
            {
                title: '示例',
                examples: ['Len("123001") 	#输出为 6']
            }
        ],
        input: [
            {
                title: 'text',
                content: '要查找其长度的文本，空格将作为字符进行计数，文本类型'
            }
        ],
        output: '返回被文本的长度，数值类型'
    },
    {
        id: 'text_contains',
        name: 'Contains',
        fn: 'Contains()',
        type: 'text',
        class: 'function',
        title: 'Contains(text, subText)',
        describe: '判断text是否包含subText文本序列',
        usage: [
            {
                title: '示例',
                examples: ['"123".Contains("13")  #输出为 false']
            },
            {
                title: '示例',
                examples: ['"12345".Contains("234") 	#输出为 true']
            }
        ],
        input: [
            {
                title: 'text',
                content: '被查找的文本，文本类型'
            },
            {
                title: 'subText',
                content: '查找的文本序列，文本类型'
            }
        ],
        output: 'true：text包含subText；false：text不包含subText，布尔类型'
    }
]

const dateFunction: FunctionObject[] = [
    {
        id: 'date_year',
        name: 'Year',
        fn: 'Year()',
        type: 'date',
        class: 'function',
        title: 'Year(time)',
        describe: '获取指定时间的年份',
        usage: [
            {
                title: '示例',
                examples: ['Year(2023/08/23 12:12:12)     #输出为 2023', '(2023/08/23 12:12:12).year()  #输出为 2023']
            }
        ],
        input: [
            {
                title: 'time',
                content: '需要获取年份的时间，为时间类型'
            }
        ],
        output: '返回指定时间的年份的数字，数值类型'
    },
    {
        id: 'date_month',
        name: 'Month',
        fn: 'Month()',
        type: 'date',
        class: 'function',
        title: 'Month(time)',
        describe: '获取指定时间在当年的月份',
        usage: [
            {
                title: '示例',
                examples: ['Month(2023/08/23 12:12:12)     #输出为 8', '(2023/08/23 12:12:12).month()  #输出为 8']
            }
        ],
        input: [
            {
                title: 'time',
                content: '需要获取月份的时间，为时间类型'
            }
        ],
        output: '返回指定时间的月份的数字，数值类型'
    },
    {
        id: 'date_day',
        name: 'Day',
        fn: 'Day()',
        type: 'date',
        class: 'function',
        title: 'Day(time)',
        describe: '获取指定时间在当月的天数',
        usage: [
            {
                title: '示例',
                examples: ['Day(2023/08/23 12:12:12)     #输出为 23', '(2023/08/23 12:12:12).day()  #输出为 23']
            }
        ],
        input: [
            {
                title: 'time',
                content: '需要获取天数的时间，为时间类型'
            }
        ],
        output: '返回指定时间在当月的天数的数字，数值类型'
    },
    {
        id: 'date_hour',
        name: 'Hour',
        fn: 'Hour()',
        type: 'date',
        class: 'function',
        title: 'Hour(time)',
        describe: '获取指定时间在当天的小时数',
        usage: [
            {
                title: '示例',
                examples: ['Hour(2022/02/02 12:23:12) = 12', '(2023/08/23 12:12:12).Hour()  #输出为 12']
            }
        ],
        input: [
            {
                title: 'time',
                content: '指定的时间'
            }
        ],
        output: '返回指定时间在当天的小时数的数字, 数值类型'
    },
    {
        id: 'date_minute',
        name: 'Minute',
        fn: 'Minute()',
        type: 'date',
        class: 'function',
        title: 'Minute(value)',
        describe: '获取指定时间在当前小时的分钟数',
        usage: [
            {
                title: '示例',
                examples: ['Minute(2023/08/23 12:23:56)        #输出为 23', '(2023/08/23 12:23:56).minute()     #输出为 23']
            }
        ],
        input: [
            {
                title: 'time',
                content: '需要获取分钟数的时间，为时间类型'
            }
        ],
        output: '指定时间分钟数的数值'
    },
    {
        id: 'date_second',
        name: 'Second',
        fn: 'Second()',
        type: 'date',
        class: 'function',
        title: 'Second(time)',
        describe: '获取指定时间在当前分钟的秒数',
        usage: [
            {
                title: '示例',
                examples: ['Second(2023/08/23 12:23:56)        #输出为 56', '(2023/08/23 12:23:56).second()     #输出为 56']
            }
        ],
        input: [
            {
                title: 'time',
                content: '需要获取秒数的时间，为时间类型'
            }
        ],
        output: '返回指定时间在当前分钟的秒数的数字，数值类型'
    },
    {
        id: 'date_now',
        name: 'Now',
        fn: 'Now()',
        type: 'date',
        class: 'function',
        title: 'Now()',
        describe: '获取当前时间',
        usage: [
            {
                title: '示例',
                examples: ['Now()    #输出为 2023/08/23 12:12:12']
            }
        ],
        input: [],
        output: '返回当前时间，时间类型'
    },
    {
        id: 'date_dateDif',
        name: 'DateDif',
        fn: 'DateDif()',
        type: 'date',
        class: 'function',
        title: 'DateDif(startTime, endTime, type)',
        describe: '获取两个日期之间相差的年，月或者天数',
        usage: [
            {
                title: '示例',
                examples: [
                    'DateDif(2022/06/24 12:12:12, 2028/09/03 12:12:12, "y")      #返回 6',
                    'DateDif(2022/06/24 12:12:12, 2028/09/03 12:12:12, "M")      #返回 74',
                    'DateDif(2022/06/24 12:12:12, 2028/09/03 12:12:12, "d")      #返回2263',
                    '(2022/06/24 12:12:12).dateDif(2028/09/03 12:12:12 , "y")    #返回 6'
                ]
            }
        ],
        input: [
            {
                title: 'startTime',
                content: '需要比较的开始时间，为时间类型'
            },
            {
                title: 'endTime',
                content: '需要比较的结束时间，为时间类型'
            },
            {
                title: 'type',
                content: [
                    '比较的方式，为文本类型，有效值包括:',
                    '"y"(计算两个时间之间的整年数)',
                    '"M"(计算两个时间之间的整月数)',
                    '"d"(计算两个时间之间的整天数)',
                    '"h"(计算两个时间之间的整小时数)',
                    '"m"(计算两个时间之间的整分钟数)',
                    '"s"(计算两个时间之间的整秒数)'
                ]
            }
        ],
        output: '返回两个时间根据条件比较的差值，数值类型'
    },
    {
        id: 'date_max',
        name: 'Max',
        fn: 'Max()',
        type: 'date',
        class: 'function',
        title: 'Max(time...)',
        describe: '找到一组时间中的最大的时间',
        usage: [
            {
                title: '示例',
                examples: ['Max(2023/09/12 12:12:12, 2024/02/21 12:12:12)  #输出为 2024/02/21 12:12:12']
            }
        ],
        input: [
            {
                title: 'time, ...',
                content: '表示任意多个时间类型的参数'
            }
        ],
        output: '返回参数中的最大的时间，时间类型'
    },
    {
        id: 'date_min',
        name: 'Min',
        fn: 'Min()',
        type: 'date',
        class: 'function',
        title: 'Min(time, ...)',
        describe: '找到一组时间中的最小的时间',
        usage: [
            {
                title: '示例',
                examples: ['Min(2023/09/12 12:12:12, 2024/02/21 12:12:12)  #输出为 2024/02/21 12:12:12']
            }
        ],
        input: [
            {
                title: 'time, ...',
                content: '表示任意多个时间类型的参数'
            }
        ],
        output: '返回参数中的最小值，数值类型'
    },
    {
        id: 'date_weekDay',
        name: 'WeekDay',
        fn: 'WeekDay()',
        type: 'date',
        class: 'function',
        title: 'WeekDay(time,[type])',
        describe: '返回指定时间在当周的第几天',
        usage: [
            {
                title: '示例',
                examples: [
                    'Weekday(2023/08/04 12:12:12)      #返回 6',
                    'Weekday(2023/08/04 12:12:12, 1)   #返回 7',
                    '(2023/08/04 12:12:12).Weekday(1)  #返回 7'
                ]
            }
        ],
        input: [
            {
                title: 'time',
                content: '指定的时间，时间类型'
            },
            {
                title: 'type',
                content:
                    '数值类型，可选参数，表示一周的第1天从星期几开始，若为 1 则该周的第 1 天从星期天开始，若为 2 则从星期一开始，默认为 1'
            }
        ],
        output: '数值类型，返回代表星期几的1-7的数字'
    },
    {
        id: 'date_weekNum',
        name: 'WeekNum',
        fn: 'WeekNum()',
        type: 'date',
        class: 'function',
        title: 'WeekNum(time,[type])',
        describe: '返回指定时间在当年的第几周',
        usage: [
            {
                title: '示例',
                examples: [
                    'WeekNum(2023/08/04 12:12:12)      #返回 31',
                    'WeekNum(2023/08/04 12:12:12, 1)   #返回 31',
                    '(2023/08/04 12:12:12).WeekNum(1)  #返回 32'
                ]
            }
        ],
        input: [
            {
                title: 'time',
                content: '指定的时间，时间类型'
            },
            {
                title: 'type',
                content:
                    '数值类型，可选参数，表示一周的第1天从星期几开始，若为 1 则该周的第 1 天从星期天开始，若为 2 则从星期一开始，默认为 1'
            }
        ],
        output: '数值类型，返回指定时间在当年的第几周'
    },
    {
        id: 'date_dateAdd',
        name: 'DateAdd',
        fn: 'DateAdd()',
        type: 'date',
        class: 'function',
        title: 'DateAdd(time, count, units)',
        describe: '为指定的日期增加固定的时间间隔',
        usage: [
            {
                title: '示例',
                examples: [
                    'DateAdd(2023/08/04 12:12:12 , 2 , "d") =  2023/08/06 12:12:12',
                    'DateAdd(2023/08/04 12:12:12 , 2 , "h") =  2023/08/06 14:12:12'
                ]
            }
        ],
        input: [
            {
                title: 'time',
                content: '时间类型，指定的时间，本函数将在该时间基础上增加或减少一定的时间间隔'
            },
            {
                title: 'count',
                content:
                    '数值类型，表示时间间隔，支持输入带正负号的数字，如果为正数，即表示增加几个时间单位 (可白定义计时单位)，如果为负数，即表示减少几个时间单位'
            },
            {
                title: 'units',
                content: [
                    '文本类型，代表计时单位，即增加时间间隔的单位，时间单位格式如下：',
                    '"秒"->"s"',
                    '"分钟"->"m"',
                    '"小时"->"h"',
                    '"天"->"d"',
                    '"周"->"w"',
                    '"月"->"M”',
                    '"年"->"y"'
                ]
            }
        ],
        output: '返回按照指定时间间隔增加或减少后的时间'
    },
    {
        id: 'date_isSame',
        name: 'IsSame()',
        fn: 'IsSame',
        type: 'date',
        class: 'function',
        title: 'IsSame(date1,date2, units)',
        describe: '根据条件判断两个时间是否相同',
        usage: [
            {
                title: '示例',
                examples: [
                    'IsSame(2023/08/04 12:12:12, 2023/08/04 18:34:12, "d")     # 比较是否是同一天, 返回 true',
                    'IsSame(2023/08/04 12:12:12, 2023/08/04 18:34:12, "h")     # 比较是否是同一小时, 返回 false',
                    '(2023/08/04 12:12:12).isSame(2023/08/04 18:34:12, "h")    # 比较是否是同一小时, 返回 false'
                ]
            }
        ],
        input: [
            {
                title: 'time1',
                content: '时间类型，需要比较的时间'
            },
            {
                title: 'time2',
                content: '时间类型，需要比较的时间'
            },
            {
                title: 'units',
                content: [
                    '比较的时间单位，时间单位格式如下：',
                    '"秒"->"s"',
                    '"分钟"->"m"',
                    '"小时"->"h"',
                    '"天"->"d"',
                    '"月"->"M”',
                    '"年"->"y"'
                ]
            }
        ],
        output: '布尔类型，如果成功则返回true'
    }
]

const logicListItemValue = '代表列表中的每一个值'

const logicFunction: FunctionObject[] = [
    {
        id: 'logic_If',
        name: 'If',
        fn: 'If()',
        type: 'logic',
        class: 'function',
        title: 'If(condition, value1, value2)',
        describe: '判断是否满足指定的条件（短路求值：只计算命中分支）',
        usage: [
            {
                title: '示例',
                examples: ['If(3>4,"正确", "错误")      #返回 "错误"', 'If(4>=3,"正确", "错误")     #返回 "正确"']
            }
        ],
        input: [
            {
                title: 'condition',
                content: '需要判断的条件，为布尔类型'
            },
            {
                title: 'value1',
                content: ' 可以是任意类型，当条件为真是返回该值 '
            },
            {
                title: 'value2',
                content: '需要与value1的类型保持一致，当条件为假是返回该值 '
            }
        ],
        output: '根据条件返回value1或者value2，返回值的类型与value1的类型相同'
    },
    {
        id: 'logic_and',
        name: 'And',
        fn: 'And()',
        type: 'logic',
        class: 'function',
        title: 'And(condition, ...)',
        describe: '判断是否满足所有的条件，与"&"符号的功能类似',
        usage: [
            {
                title: '示例',
                examples: ['And(3>4, 3>2, 5>3) #返回 false', 'And(6>4, 3>2, 5>3) #返回 true', '6>4 & 3>2 & 5>3 #返回 true']
            }
        ],
        input: [
            {
                title: 'condition, ...',
                content: '一组需要判断的条件，为布尔类型'
            }
        ],
        output: '布尔类型，当所有条件都正确时返回true，有任意一个条件错误则返回false'
    },
    {
        id: 'logic_or',
        name: 'Or',
        fn: 'Or()',
        type: 'logic',
        class: 'function',
        title: 'Or(condition, ...)',
        describe: '判断是否满足任意一个条件，与"|"符号的功能类似',
        usage: [
            {
                title: '示例',
                examples: ['Or(3>4, 3>7, 5>9) #返回 false', 'Or(6>4, 3<2, 5<3) #返回 true', '6>4 | 3<2 | 5<3 #返回 true']
            }
        ],
        input: [
            {
                title: 'condition, ...',
                content: '一组需要判断的条件，为布尔类型'
            }
        ],
        output: '当任意一个条件正确时返回true，所有条件都错误则返回false'
    },
    {
        id: 'logic_not',
        name: 'Not',
        fn: 'Not()',
        type: 'logic',
        class: 'function',
        title: 'Not(condition)',
        describe: '对输入的条件取反',
        usage: [
            {
                title: '示例',
                examples: ['Not(3>4) #返回 true', 'Not(6>4) #返回 false']
            }
        ],
        input: [
            {
                title: 'condition',
                content: '需要取反的条件，为布尔类型'
            }
        ],
        output: '布尔类型，当输入的条件为真时，返回false，当输入的条件为假时，返回true'
    },
    {
        id: 'logic_switch',
        name: 'Switch',
        fn: 'Switch()',
        type: 'logic',
        class: 'function',
        title: 'Switch(expression, [pattern,result]... , [default])',
        describe: '分支选择函数，它由表达式 + 多个 [分支值 + 返回值] 组成，如果表达式等于某个分支值，则函数输出该分支对应的返回值',
        usage: [
            {
                title: '示例',
                examples: [
                    'Switch(Year(2023/08/12 12:12:12), 2022, "过去", 2023, "现在", 2088, "将来")  #返回 "现在"',
                    'Switch(Year(2000/08/12 12:12:12), 2022, "过去", 2023, "现在", 2088, "将来", "无匹配的时间")  #返回 "无匹配的时间"'
                ]
            }
        ],
        input: [
            {
                title: 'expression',
                content: '需要进行分支匹配的表达式或值，可以是任意类型'
            },
            {
                title: 'pattern',
                content: '当前分支的值，用于与expression的值进行比较'
            },
            {
                title: 'result',
                content: '当前分支对应的值，如果当前分支匹配成功，则返回该值，可以是任意类型，但所有分支中的result都需要是同一类型'
            },
            {
                title: 'default',
                content: '如果所有分支都未匹配，则返回该值'
            }
        ],
        output: '返回第一个匹配成功的分支所对应的值，返回值类型与第一个分支对应的值一致，如果所有分支都未匹配，则返回default参数对应的值'
    },
    {
        id: 'logic_switchIf',
        name: 'SwitchIf',
        fn: 'SwitchIf()',
        type: 'logic',
        class: 'function',
        title: 'SwitchIf([condition,result]... , [default])',
        describe: '多分支选择函数，它由表达式 +多个 [条件分支 + 返回值] 组成，如果表达式符合某个分支的条件，则函数输出该分支对应的返回值',
        usage: [
            {
                title: '示例',
                examples: [
                    'SwitchIf(成绩<60,"不及格”,And(成绩>=60,成绩<80),"良好","优秀")  #当成绩是70时返回 "良好" ，当成绩是90时，返回 "优秀"'
                ]
            }
        ],
        input: [
            {
                title: 'condition',
                content: '需要判断的分支条件，布尔类型'
            },
            {
                title: 'result',
                content: '当前分支对应的值，如果当前分支匹配成功，则返回该值，可以是任意类型，但所有分支中的result都需要是同一类型'
            },
            {
                title: 'default',
                content: '如果所有分支都未匹配，则返回该值'
            }
        ],
        output: '返回第一个匹配成功的分支所对应的值，返回值类型与第一个分支对应的值一致，如果所有分支都未匹配，则返回default参数对应的值'
    }
    // {
    //     id: 'isBlank',
    //     name: 'IsBlank()',
    //     type: 'logic',
    //     class: 'function',
    //     title: 'IsBlank(value)',
    //     describe: '判断指定的值是否为空',
    //     usage: [
    //         {
    //             title: '示例',
    //             examples: ['IsBlank("") #返回 true', '"".isBlank() #返回 true', '[].isBlank()         #返回 true', 'IsBlank([].first())  #返回 true]
    //         }
    //     ],
    //     input: [
    //         {
    //             title: 'value',
    //             content: '需要判断的值，可以是任意类型'
    //         }
    //     ],
    //     output: '当指定的值为空时返回true，否则返回false，当一段公式出现一些数据错误导致返回为空时，该函数也能正确判断其是否为空'
    // }
]

const arrayFunction: FunctionObject[] = [
    {
        id: 'array_contains',
        name: 'Contains',
        fn: 'Contains()',
        type: 'array',
        class: 'function',
        title: 'Contains(array, value)',
        describe: '判断数组中是否包含指定的元素',
        usage: [
            {
                title: '示例',
                examples: [
                    'Contains(["今天", "明天", "后天"], "明天")   #返回true',
                    '["今天", "明天", "后天"].contains("明天")    #返回true'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要查找的数组，数组类型'
            },
            {
                title: 'value',
                content: '需要查找的元素'
            }
        ],
        output: '如果包含指定元素则返回true，否则返回false，布尔类型'
    },
    {
        id: 'array_containsAll',
        name: 'ContainsAll',
        fn: 'ContainsAll()',
        type: 'array',
        class: 'function',
        title: 'ContainsAll(array, value...)',
        describe: '判断数组中是否包含指定的所有元素',
        usage: [
            {
                title: '示例',
                examples: [
                    'ContainsAll(["今天", "明天", "后天"], "今天" ,"明天") #返回 false',
                    'ContainsAll(["今天", "明天", "后天"], "今天", "明天", "后天")  #返回 true',
                    '["今天", "明天", "后天"].containsAll("今天", "明天")#返回 false'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要查找的数组，数组类型'
            },
            {
                title: 'value',
                content: '指定的一组需要查找的值'
            }
        ],
        output: '如果包含指定元素则返回true，否则返回false，布尔类型'
    },
    {
        id: 'array_count',
        name: 'Count',
        fn: 'Count()',
        type: 'array',
        class: 'function',
        title: 'Count(value)',
        describe: '统计数组中一共有几个元素',
        usage: [
            {
                title: '示例',
                examples: ['Count([1,2,3,4,5]) # 返回 5', '[1,2,3,4,5].count() # 返回 5']
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要统计的数组，数组类型'
            }
        ],
        output: '数值类型，返回数组中元素的个数'
    },
    {
        id: 'array_append',
        name: 'Append',
        fn: 'Append()',
        type: 'array',
        class: 'function',
        title: 'Append(array, value)',
        describe: '向数组的尾部追加一个元素',
        usage: [
            {
                title: '示例',
                examples: ['Append([1, 2, 3], 4) 	#输出为 [1, 2, 3, 4]', '[1, 2, 3].Append(4)  #输出为[1, 2, 3, 4]']
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要追加的数组，数组类型'
            },
            {
                title: 'value',
                content: '要追加到数组尾部的元素'
            }
        ],
        output: '数组类型，返回追加元素后的数组'
    },
    {
        id: 'array_remove',
        name: 'Remove',
        fn: 'Remove()',
        type: 'array',
        title: 'Remove(array, value)',
        class: 'function',
        describe: '删除数组中第一个匹配目标值的元素',
        usage: [
            {
                title: '示例',
                examples: ['Remove([1, 2, 3, 2], 2) 	#输出为 [1, 3, 2]', '[1, 2, 3, 2].Remove(2)  #输出为 [1, 3, 2]']
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要移除元素的数组，数组类型'
            },
            {
                title: 'value',
                content: '要删除的元素值'
            }
        ],
        output: '数组类型，返回删除第一个匹配元素后的数组'
    },
    {
        id: 'array_update',
        name: 'Update',
        fn: 'Update()',
        class: 'function',
        type: 'array',
        title: 'Update(array, index, value)',
        describe: '改数组指定索引位置的元素值',
        usage: [
            {
                title: '示例',
                examples: [
                    'Update(["1", "2", "3"], 1, "200") 	#输出为 ["1", "200", "3"]',
                    '["1", "2", "3"].Update(1, "200")  #输出为 ["1", "200", "3"]'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要更新元素的数组，数组类型'
            },
            {
                title: 'index',
                content: '要修改元素的索引（从 0 开始计数）'
            },
            {
                title: 'value',
                content: '该索引位置要设置的新元素值'
            }
        ],
        output: '数组类型，返回修改指定元素后的数组'
    },
    {
        id: 'array_findIndex',
        name: 'FindIndex',
        fn: 'FindIndex()',
        class: 'function',
        type: 'array',
        title: 'FindIndex(array, condition)',
        describe: '查找数组中第一个满足条件的元素的索引',
        usage: [
            {
                title: '示例',
                examples: [
                    'FindIndex(["10", "20", "30"], {currentValue} != "20") 	#输出为 1',
                    '["10", "20", "30"].FindIndex({currentValue} == "20")  #输出为 1'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要查找元素的数组，数组类型'
            },
            {
                title: 'condition',
                content: '判断元素是否匹配的条件（返回布尔值）其中有特殊字符{currentValue}，表示当前元素'
            }
        ],
        output: '数值类型，返回第一个满足条件的元素的索引；若未找到匹配元素，返回 - 1'
    },
    {
        id: 'array_filter',
        name: 'Filter',
        fn: 'Filter()',
        class: 'function',
        type: 'array',
        title: 'Filter(array, condition)',
        describe: '按照条件对数组进行过滤',
        usage: [
            {
                title: '示例',
                examples: [
                    'Filter(["10", "20", "30"], {currentValue} != "20")  #输出为 ["10", "30"]',
                    '["1","2","3","4","5"].Filter({currentValue} == "3")  = ["3"]'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要查找元素的数组，数组类型'
            },
            {
                title: 'condition',
                content: '判断元素是否匹配的条件（返回布尔值）其中有特殊字符{currentValue}，表示当前元素'
            }
        ],
        output: '数组类型，返回过滤元素后的数组'
    },
    {
        id: 'array_first',
        name: 'First',
        fn: 'First()',
        type: 'array',
        class: 'function',
        title: 'First(array)',
        describe: '查找并返回数组中的第一个元素',
        usage: [
            {
                title: '示例',
                examples: ['First([1,2,3,4,5]) # 返回 1', '[1,2,3,4,5].first() # 返回 1']
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要查找的数组'
            }
        ],
        output: '数组中的第一个元素，返回的类型与数组中的元素的类型一致'
    },
    {
        id: 'array_sum',
        name: 'Sum',
        fn: 'Sum()',
        type: 'array',
        class: 'function',
        title: 'Sum(array)',
        describe: '统计数组中所有数值的总和，该函数要求数组中的元素必须是数值类型',
        usage: [
            {
                title: '示例',
                examples: ['Sum([1,2,3,4,5]) # 返回 15', '[1,2,3,4,5].sum()       # 返回 15']
            }
        ],
        input: [
            {
                title: 'array',
                content: ' 需要统计的数组'
            }
        ],
        output: '数值类型，返回统计后的结果'
    },
    {
        id: 'array_max',
        name: 'Max',
        fn: 'Max()',
        type: 'array',
        class: 'function',
        title: 'Max(array)',
        describe: '查找数值中元素的最大值，如果数组中的元素是数值类型，则返回最大的数值，如果数组中的元素是日期类型，则返回最大的日期',
        usage: [
            {
                title: '示例',
                examples: [
                    'Max([1,2,3,4,5]) #返回 5',
                    '[1,2,3,4,5].max() #返回 5',
                    'Max([2022/02/21 12:12:12, 2023/08/23 12:12:12])  #返回  2023/08/23 12:12:12'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要统计的数组，数组中的元素可以是数值类型或者日期类型'
            }
        ],
        output: '返回统计后的结果，如果数组中的元素是数值类型，则返回类型为数值类型，如果数组中的元素为日期类型，则返回类型为日期类型'
    },
    {
        id: 'array_min',
        name: 'Min',
        fn: 'Min()',
        type: 'array',
        class: 'function',
        title: 'Min(array)',
        describe: '查找数值中元素的最小值，如果数组中的元素是数值类型，则返回最小的数值，如果数组中的元素是日期类型，则返回最小的日期',
        usage: [
            {
                title: '示例',
                examples: [
                    'Min([1,2,3,4,5]) #返回 1',
                    '[1,2,3,4,5].min() #返回 1',
                    'Min([2022/02/21 12:12:12, 2023/08/23 12:12:12])  #返回  2022/02/21 12:12:12'
                ]
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要统计的数组，数组中的元素可以是数值类型或者日期类型'
            }
        ],
        output: '返回统计后的结果，如果数组中的元素是数值类型，则返回类型为数值类型，如果数组中的元素为日期类型，则返回类型为日期类型'
    },
    {
        id: 'array_avg',
        name: 'Avg',
        fn: 'Avg()',
        type: 'array',
        class: 'function',
        title: 'Avg(array)',
        describe: '统计数组中所有数值的平均值，该函数要求数组中的元素必须是数值类型',
        usage: [
            {
                title: '示例',
                examples: ['Avg([1,2,3,4,5]) # 返回 3', '[1,2,3,4,5].avg() # 返回 3']
            }
        ],
        input: [
            {
                title: 'array',
                content: '需要统计的数组'
            }
        ],
        output: '数值类型，返回统计后的结果'
    }
]

const otherFunction: FunctionObject[] = [
    {
        id: 'other_isBlank',
        name: 'IsBlank',
        fn: 'IsBlank()',
        type: 'other',
        class: 'function',
        title: 'IsBlank(value)',
        describe: '判断指定的值是否为空',
        usage: [
            {
                title: '示例',
                examples: [
                    'IsBlank("") #返回 true',
                    '"".isBlank() #返回 true',
                    '[].isBlank()         #返回 true',
                    'IsBlank([].first())  #返回 true'
                ]
            }
        ],
        input: [
            {
                title: 'value',
                content: '需要判断的值，可以是任意类型'
            }
        ],
        output: '当指定的值为空时返回true，否则返回false，当一段公式出现一些数据错误导致返回为空时，该函数也能正确判断其是否为空'
    }
]

export const innerTypeToMethods: Record<TypeInstanceMap, string[]> = {
    TEXT: ['text', 'other'],
    NUMBER: ['number', 'other'],
    ARRAY: ['array', 'other'],
    DATE: ['date', 'other'],
    BOOL: ['other'],
    RICH_TEXT: ['other'],
    NULL: [],
    RUNTIME: []
}

export const methodList: MethodList[] = [
    {
        type: 'number',
        name: '数值函数',
        list: numberFunction
    },
    {
        type: 'text',
        name: '文本函数',
        list: textFunction
    },
    {
        type: 'date',
        name: '日期函数',
        list: dateFunction
    },
    {
        type: 'logic',
        name: '逻辑函数',
        list: logicFunction
    },
    {
        type: 'array',
        name: '数组函数',
        list: arrayFunction
    },
    {
        type: 'other',
        name: '其他函数',
        list: otherFunction
    }
]
